'use client'

import { useState } from 'react'
import { Download, Copy, Check } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { useToolUsage } from '@/lib/use-tool-usage'
import { useToolAccessGate } from '@/components/tools/ToolAccessGate'
import { UsageLimitGate } from '@/components/tools/UsageLimitGate'
import ToolLlmsTxtGeneratorRef from '@/components/reference/ToolLlmsTxtGeneratorRef'
import { useUrlRun } from '@/components/reference/tool-run'
import '@/components/reference/tool-page.css'
import { ToolLiveStatus } from '@/components/reference/tool-live'
import '@/components/reference/tool-live.css'
import { useScrollIntoView } from '@/hooks/useScrollIntoView'

interface GeneratorResult {
  url: string
  domain: string
  generatedAt: string
  llmsTxt: string
  extracted: {
    orgName: string
    hasTagline: boolean
    hasDescription: boolean
    productCount: number
    topicCount: number
    keyUrlCount: number
    hasSitemap: boolean
    hasContact: boolean
    aiAssisted: boolean
  }
}

// Build the "Before you publish" checklist from what the generator could and
// could not detect. These are the gaps a hosted llms.txt should not ship with,
// so they live in the UI, never inside the generated file.
function publishChecklist(e: GeneratorResult['extracted']): string[] {
  const items: string[] = []
  if (!e.hasContact) {
    items.push(
      'Add your contact email on the Contact line (we left a placeholder).'
    )
  }
  if (e.aiAssisted && e.productCount > 0) {
    items.push(
      `Review the ${e.productCount} product description${e.productCount === 1 ? '' : 's'} we drafted from your pages for accuracy and tone.`
    )
  }
  if (e.aiAssisted && e.hasDescription) {
    items.push(
      'Confirm the company overview matches how you position yourself.'
    )
  }
  items.push(
    'Add details we cannot read from your pages: founding year, headquarters, pricing model, and key differentiators.'
  )
  if (e.keyUrlCount < 5) {
    items.push(
      'Add any other important pages to Key URLs (docs, security, customers).'
    )
  }
  if (!e.hasSitemap) {
    items.push('Publish a sitemap at /sitemap.xml and add it to Key URLs.')
  }
  return items
}

export default function LlmsTxtGeneratorPage() {
  const scrollIntoView = useScrollIntoView()
  const toolUsage = useToolUsage('llms-txt-generator')
  const gate = useToolAccessGate('llms-txt-generator', 'llms.txt Generator', {
    variant: 'reference',
  })
  const [result, setResult] = useState<GeneratorResult | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  // Input validation lives in ToolEntryHero; this receives the trimmed URL.
  async function handleSubmit(trimmed: string) {
    if (!gate.ready) {
      // Resumes after the code verifies, so the typed URL runs (PIX-47).
      gate.openGate(trimmed, () => void runCheck(trimmed))
      return
    }

    await runCheck(trimmed)
  }

  async function runCheck(trimmed: string) {
    setIsLoading(true)
    setError(null)
    setResult(null)
    try {
      const response = await toolUsage.fetchWithUsage(
        '/api/tools/llms-txt-generator',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: trimmed }),
        }
      )
      const data = await response.json()
      if (
        response.status === 403 &&
        (data.error === 'usage_limit' || data.error === 'gate_required')
      ) {
        gate.openGate(trimmed, () => void runCheck(trimmed))
        return
      }

      if (response.status === 429 && data.error === 'domain_cap') {
        gate.showDomainCap(data)
        return
      }
      if (!response.ok) {
        throw new Error(data.message || 'Generation failed. Please try again.')
      }
      setResult(data as GeneratorResult)
      gate.markConsumed()
      scrollIntoView('generator-result')
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Something went wrong. Please try again.'
      )
    } finally {
      setIsLoading(false)
    }
  }

  function copyToClipboard() {
    if (!result) return
    navigator.clipboard.writeText(result.llmsTxt)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  function download() {
    if (!result) return
    const blob = new Blob([result.llmsTxt], { type: 'text/plain' })
    const href = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = href
    a.download = 'llms.txt'
    a.click()
    URL.revokeObjectURL(href)
  }

  const run = useUrlRun({
    example: 'pixelmojo.io',
    isLoading: isLoading,
    error: error,
    onSubmit: handleSubmit,
  })

  return (
    <div className='shell pg-tool pg-tool-llms-txt-generator'>
      {gate.modal}
      <ToolLlmsTxtGeneratorRef
        run={run}
        output={
          <>
            <div id='generator-result' className='max-w-3xl mx-auto'>
              {isLoading && (
                <ToolLiveStatus tool='llms.txt Generator'>
                  Reading your site and building your llms.txt...
                </ToolLiveStatus>
              )}

              {toolUsage.isGated && !isLoading && (
                <UsageLimitGate
                  variant='reference'
                  toolName='llms.txt Generator'
                  used={toolUsage.used}
                  limit={toolUsage.limit}
                />
              )}

              {result && !isLoading && !toolUsage.isGated && (
                <>
                  <div className='bg-muted/50 rounded-2xl border border-line-strong p-6 md:p-8'>
                    <div className='flex flex-wrap items-center justify-between gap-3 mb-4'>
                      <div>
                        <p className='text-sm font-semibold text-foreground leading-none'>
                          llms.txt for {result.domain}
                        </p>
                        <p className='text-xs text-muted-foreground mt-1.5'>
                          {result.extracted.productCount} product
                          {result.extracted.productCount === 1 ? '' : 's'},{' '}
                          {result.extracted.keyUrlCount} key URL
                          {result.extracted.keyUrlCount === 1 ? '' : 's'}{' '}
                          detected. Fill any [bracketed] gaps before publishing.
                        </p>
                      </div>
                      <div className='flex gap-2'>
                        <Button
                          type='button'
                          variant='outline'
                          size='sm'
                          onClick={copyToClipboard}
                        >
                          {copied ? (
                            <Check className='w-4 h-4' />
                          ) : (
                            <Copy className='w-4 h-4' />
                          )}
                          {copied ? 'Copied' : 'Copy'}
                        </Button>
                        <Button
                          type='button'
                          variant='cta'
                          size='sm'
                          onClick={download}
                        >
                          <Download className='w-4 h-4' />
                          Download
                        </Button>
                      </div>
                    </div>
                    <pre className='bg-background border border-line-strong rounded-xl p-4 text-xs md:text-sm text-foreground/90 overflow-x-auto max-h-[480px] overflow-y-auto whitespace-pre-wrap break-words font-mono'>
                      {result.llmsTxt}
                    </pre>
                    <p className='text-xs text-muted-foreground mt-3'>
                      Host this at{' '}
                      <span className='font-mono text-foreground/80'>
                        {result.url}/llms.txt
                      </span>
                      , then validate it with the llms.txt Validator.
                    </p>
                  </div>
                  <div className='bg-muted/50 rounded-2xl border border-line-strong p-6 md:p-8 mt-4'>
                    <p className='text-sm font-semibold text-foreground leading-none'>
                      Before you publish this file
                    </p>
                    <p className='text-xs text-muted-foreground mt-1.5 mb-4'>
                      We built this from what your pages expose. Finish these so
                      AI engines get the full, accurate picture. None of it is
                      in the file yet, so add it before hosting.
                    </p>
                    <ul className='space-y-2.5'>
                      {publishChecklist(result.extracted).map((item, i) => (
                        <li
                          key={i}
                          className='flex items-start gap-2.5 text-sm text-foreground/90'
                        >
                          <span
                            className='mt-1.5 inline-block w-1.5 h-1.5 shrink-0 bg-[#008dcc]'
                            aria-hidden='true'
                          />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              )}
            </div>
          </>
        }
      />
    </div>
  )
}
